import React, { useEffect, useState } from 'react';

// Чистый компонент бейджа категории
const CategoryBadge = ({ category }) => {
  switch (category) {
    case 'promo':
      return <span className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[10px] uppercase font-bold px-2.5 py-0.5 rounded-full">🔥 Акция</span>;
    case 'info':
      return <span className="bg-red-500/20 text-red-400 border border-red-500/30 text-[10px] uppercase font-bold px-2.5 py-0.5 rounded-full">📢 Важное</span>;
    default:
      return <span className="bg-blue-500/20 text-blue-400 border border-blue-500/30 text-[10px] uppercase font-bold px-2.5 py-0.5 rounded-full">🚀 Обновление</span>;
  }
};

const NewsModal = ({ onClose }) => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Все новости по умолчанию свернуты
  const [expandedIds, setExpandedIds] = useState([]);

  useEffect(() => {
    fetch('/api/news/')
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error('Ошибка загрузки');
      })
      .then((data) => {
        setNewsList(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Не удалось загрузить новости:', err);
        setLoading(false);
      });
  }, []);

  // Переключение состояния (раскрыто/свернуто)
  const toggleNews = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
    >
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl shadow-2xl flex flex-col overflow-hidden h-[650px] max-h-[90vh]">
        
        {/* Шапка модального окна */}
        <div className="p-5 bg-slate-900/90 border-b border-slate-800 flex justify-between items-center z-10">
          <div>
            <h3 className="font-bold text-white text-lg flex items-center gap-2">
              📰 Новости и Акции
            </h3>
            <p className="text-xs text-slate-400">Обновления платформы и специальные предложения</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white text-xl font-bold w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-800 transition cursor-pointer"
          >
            ✕
          </button>
        </div>

        {/* Список новостей (Аккордеон) */}
        <div className="flex-1 p-5 overflow-y-auto space-y-3 bg-slate-950/50 scrollbar-thin scrollbar-thumb-slate-800">
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-500 text-xs italic">
              Загрузка новостей...
            </div>
          ) : newsList.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-2">
              <span className="text-3xl">📭</span>
              <p className="text-xs italic">Пока нет опубликованных новостей.</p>
            </div>
          ) : (
            newsList.map((item) => {
              const isExpanded = expandedIds.includes(item.id);

              return (
                <div
                  key={item.id}
                  className={`bg-slate-900/90 border rounded-2xl overflow-hidden transition-all duration-200 ${
                    item.is_pinned
                      ? 'border-indigo-500/40 bg-indigo-950/10'
                      : 'border-slate-800/80 hover:border-slate-700'
                  }`}
                >
                  {/* Заголовок-аккордеон (Кликабельный) */}
                  <div
                    onClick={() => toggleNews(item.id)}
                    className="p-4 flex items-start justify-between gap-3 cursor-pointer select-none hover:bg-slate-800/30 transition duration-150"
                  >
                    <div className="space-y-1.5 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {item.is_pinned && (
                          <span className="text-[10px] text-indigo-400 font-bold bg-indigo-500/10 px-2 py-0.5 rounded-md border border-indigo-500/20">
                            📌 Закреплено
                          </span>
                        )}
                        <CategoryBadge category={item.category} />
                        <span className="text-[11px] text-slate-500 font-mono">
                          {item.created_at
                            ? new Date(item.created_at).toLocaleDateString('ru-RU', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })
                            : ''}
                        </span>
                      </div>

                      <h4 className="font-bold text-white text-base leading-snug">
                        {typeof item.title === 'string' ? item.title : String(item.title || '')}
                      </h4>
                    </div>

                    {/* Стрелочка индикатора раскрытия */}
                    <div className="text-slate-400 pt-1">
                      <svg
                        className={`w-5 h-5 transform transition-transform duration-200 ${
                          isExpanded ? 'rotate-180 text-indigo-400' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Раскрываемый контент */}
                  {isExpanded && (
                    <div className="px-4 pb-5 pt-2 border-t border-slate-800/50 space-y-3 animate-in fade-in duration-300">
                      {item.image && typeof item.image === 'string' && (
                        <div className="rounded-xl overflow-hidden border border-slate-800 bg-slate-950 max-h-72 my-2">
                          <img
                            src={item.image}
                            alt={typeof item.title === 'string' ? item.title : ''}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-line">
                        {typeof item.content === 'string' ? item.content : String(item.content || '')}
                      </p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

      </div>
    </div>
  );
};

export default NewsModal;